import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { AutocompleteDropdownComponent } from '@app/shared/autocomplete-dropdown/autocomplete-dropdown.component';
import { ContentBoxComponent } from '@app/shared/content-box/content-box.component';
import { Labels } from '@core/constants/labels';
import { Image } from '@core/models/image.model';
import { Patient } from '@core/models/patient.model';
import { User } from '@core/models/user.model';
import { DiscussionService } from '@core/services/discussion-service/discussion-service.service';
import { ExamService } from '@core/services/exam-service/exam-service.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { RegisterImageComponent } from '../register-image/register-image.component';
import { CreatePatientDialogComponent } from './create-patient-dialog/create-patient-dialog.component';
// TODO: validation
@Component({
  selector: 'app-create-exam',
  standalone: true,
  imports: [
    AutocompleteDropdownComponent,
    CommonModule,
    ContentBoxComponent,
    MatAutocompleteModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    RegisterImageComponent,
  ],
  templateUrl: './create-exam.component.html',
  styleUrl: './create-exam.component.scss',
})
export class CreateExamComponent implements OnInit, OnDestroy {
  examForm = new FormGroup({
    id: new FormControl(-1),
    patient: new FormControl(),
    description: new FormControl(''),
    urgency: new FormControl(),
    assigned_to: new FormControl(),
    tissue_related: new FormControl(),
  });

  imageData: {
    image: Image;
    id: number;
    ready: boolean;
    name: string;
    currentlyUploading: boolean;
    file: any;
  }[] = [];

  labels = Labels.REGISTER_EXAM;

  prontuary = [];
  images = [];

  prontuaryFile: any = null;

  subscriptions = new Subscription();

  allPatients: Patient[] = [];
  allDoctors: User[] = [];

  allTissueRegions = this.labels.TISSUE_REGION_OPTIONS;

  doctorsLoaded = false;

  selectedPatient: Patient | null = null;
  selectedTissue: any = null;
  selectedAssignedTo: any = null;

  constructor(
    private examService: ExamService,
    private router: Router,
    private toastrService: ToastrService,
    private dialog: MatDialog,
    private discussionService: DiscussionService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.updatePatientsAndDoctors(null);
    const id = this.route.snapshot.paramMap.get('id');
    if (id !== null) {
      this.examService.getExam(+id).subscribe((data) => {
        const filteredTissueRegions = this.allTissueRegions.filter(
          (a) => a.value === data?.tissue_related,
        );
        this.examForm.patchValue({
          id: data?.id,
          patient: data?.patient,
          description: data?.description,
          urgency: data?.urgent
            ? this.labels.URGENCY_YES
            : this.labels.URGENCY_NO,
          assigned_to: data?.assigned_to,
          tissue_related: filteredTissueRegions?.length
            ? filteredTissueRegions[0].value
            : null,
        });

        if (data?.patient !== undefined) {
          this.selectedPatient = data?.patient;
        }
        this.selectedTissue = filteredTissueRegions[0];
        this.selectedAssignedTo = data.assigned_to;

        this.imageData = data.images.map((i) => {
          return {
            image: i,
            id: i.id,
            ready: true,
            name: i.name,
            currentlyUploading: false,
            file: null,
          };
        });
      });
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  updatePatientsAndDoctors(selectedPatient: Patient | null) {
    const patientsSub = this.examService.getPatients().subscribe(
      (patients) => {
        this.allPatients = patients;
        if (selectedPatient !== null) {
          this.selectedPatient = selectedPatient;
          this.toastrService.success(this.labels.CREATE_PATIENT_SUCCESS);
        }
      },
      (error) => {
        console.error('Error loading patients:', error);
        this.allPatients = [];
      },
    );

    if (!this.doctorsLoaded) {
      this.doctorsLoaded = true;
      const doctorsSub = this.discussionService.getDoctors().subscribe(
        (doctors) => {
          this.allDoctors = doctors;
        },
        (error) => {
          console.error('Error loading doctors:', error);
          this.allDoctors = [];
        },
      );
      this.subscriptions.add(doctorsSub);
    }

    this.subscriptions.add(patientsSub);
  }

  invalidAndTouched(key: string): boolean {
    const field: any = this.examForm?.get(key);
    if (field === null) return false;
    return field.touched && !field.valid;
  }

  handleImagesChanged(newImageData: any[]) {
    this.imageData = newImageData;
  }

  sendExam() {
    if (!this.examForm.get('patient')?.value) {
      this.toastrService.error(this.labels.ERROR_MISSING_PATIENT);
      return;
    } else if (!this.examForm.get('urgency')?.value) {
      this.toastrService.error(this.labels.ERROR_MISSING_URGENCY);
      return;
    } else if (this.imageData.length === 0) {
      this.toastrService.error(this.labels.ERROR_MISSING_IMAGES);
      return;
    } else if (!this.examForm.get('tissue_related')?.value) {
      this.toastrService.error(this.labels.ERROR_MISSING_TISSUE_REGION);
      return;
    }

    const imageIds = this.imageData.filter((i) => i.id > 0).map((i) => i.id);
    const sub = this.examService
      .saveExam(this.examForm.value, imageIds, this.prontuaryFile)
      .subscribe(
        (x) => {
          this.toastrService.success(this.labels.SUCCESS);
          this.router.navigate(['']);
        },
        (err) => {
          this.toastrService.error(
            this.labels.ERROR_CREATING + JSON.stringify(err),
          );
        },
      );
    this.subscriptions.add(sub);
  }

  handleProntuaryChange(event: any): void {
    this.prontuaryFile = event.target.files[0];
  }

  openCreatePatientDialog(): void {
    const dialogRef = this.dialog.open(CreatePatientDialogComponent, {
      width: '400px',
    });

    const sub = dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.updatePatientsAndDoctors(result);
      }
    });
    this.subscriptions.add(sub);
  }

  displayPatientFn = (patient: Patient): string => {
    return patient && patient.name ? patient.name : '';
  };

  displayDoctorFn = (doctor: User): string => {
    return doctor && doctor.full_name ? doctor.full_name : '';
  };

  displayTissueRegionFn = (regionData: any): string => {
    return regionData?.label ?? '';
  };

  onPatientSelectionChange(patient: Patient | null) {
    this.examForm.get('patient')?.setValue(patient);
  }

  onDoctorSelectionChange(doctor: User | null) {
    this.examForm.get('assigned_to')?.setValue(doctor);
  }

  onTissueRegionSelectionChange(regionData: any) {
    this.examForm.get('tissue_related')?.setValue(regionData?.value ?? '');
  }
}
